import { MentionNodeAttrs } from '@tiptap/extension-mention';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { UserAvatar } from '@/components/treatment/v3/user/UserAvatar';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';

interface MentionItem {
  id: string;
  label: string; // The username (e.g., "johndoe")
  value: string; // The full name (e.g., "John Doe")
}

interface TaskUpdateMentionListProps {
  items: MentionItem[];
  command: (item: Required<MentionNodeAttrs>) => void;
}

interface MentionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

export const TaskUpdateMentionList = forwardRef<
  MentionListRef,
  TaskUpdateMentionListProps
>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const commandRef = useRef<HTMLDivElement>(null);

  const selectItem = (value: string) => {
    const foundItem = props.items.find((item) => item.id === value);
    if (foundItem) {
      props.command({
        id: foundItem.id,
        label: foundItem.label, // Insert username into the editor (renderer adds @)
      });
    }
  };
  useEffect(() => {
    setSelectedIndex(0);
  }, [props.items]);

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelectedIndex(
          (current) => (current + props.items.length - 1) % props.items.length,
        );
        return true;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSelectedIndex((current) => (current + 1) % props.items.length);
        return true;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        const selectedItem = props.items[selectedIndex];
        if (selectedItem) {
          selectItem(selectedItem.id);
        }
        return true;
      }

      return false;
    },
  }));

  return (
    <Command
      ref={commandRef}
      className="rounded-lg bg-white border shadow-md relative z-[100] overflow-hidden min-w-[200px]"
    >
      <CommandList className="max-h-[200px] overflow-y-auto py-0">
        <CommandGroup className="h-full">
          {props.items.length ? (
            props.items.map((item, index) => (
              <CommandItem
                key={item.id}
                value={item.id}
                onSelect={() => selectItem(item.id)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={cn(
                  'cursor-pointer transition-colors hover:bg-gray-100 font-normal text-sm p-1.5 flex items-center gap-2',
                  index === selectedIndex && 'bg-blue-100',
                )}
              >
                <UserAvatar userId={item.id} size="md" />
                <div className="flex flex-col">
                  <div className="font-medium text-gray-900">{item.label}</div>
                  <div
                    className={cn(
                      'text-xs text-gray-500',
                      index === selectedIndex && 'text-gray-700',
                    )}
                  >
                    {item.value}
                  </div>
                </div>
              </CommandItem>
            ))
          ) : (
            <div className="p-2 text-sm text-gray-500">No users found</div>
          )}
        </CommandGroup>
      </CommandList>
    </Command>
  );
});

TaskUpdateMentionList.displayName = 'TaskUpdateMentionList';
